import React from 'react';
import { Layout } from '../components/layout/Layout';
import { useLanguage } from '../context/LanguageContext';
import { MapPin, Building2, GraduationCap, Globe } from 'lucide-react';

const NetworkPage = () => {
  const { t } = useLanguage();

  const representations = [
    { country: 'République Centrafricaine', city: 'Bangui', status: 'Siège', code: 'CF' },
    { country: 'Mali', city: 'Bamako', status: 'Bureau régional', code: 'ML' },
    { country: 'Burkina Faso', city: 'Ouagadougou', status: 'Bureau régional', code: 'BF' },
    { country: 'Cameroun', city: 'Yaoundé', status: 'Bureau régional', code: 'CM' },
    { country: 'Niger', city: 'Niamey', status: 'Représentation', code: 'NE' },
    { country: 'Tchad', city: "N'Djamena", status: 'Représentation', code: 'TD' },
  ];

  const partnerCenters = [
    { name: 'Centre Culturel Russophone de Bangui', country: 'Centrafrique', type: 'Culture' },
    { name: 'Institut de Langue Russe de Bamako', country: 'Mali', type: 'Éducation' },
    { name: 'Centre de Formation Technique de Ouagadougou', country: 'Burkina Faso', type: 'Formation' },
    { name: 'Maison Afrique-Russie de Yaoundé', country: 'Cameroun', type: 'Diplomatie' },
  ];

  const universities = [
    { name: 'Université d\'État de Moscou (MGU)', country: 'Russie', programs: '15+' },
    { name: 'Université RUDN', country: 'Russie', programs: '20+' },
    { name: 'Université de Saint-Pétersbourg', country: 'Russie', programs: '12+' },
    { name: 'Université Polytechnique de Tomsk', country: 'Russie', programs: '8+' },
    { name: 'Université de Kazan', country: 'Russie', programs: '10+' },
    { name: 'Université de Pékin', country: 'Chine (BRICS)', programs: '5+' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-32 md:py-40 bg-oar-blue-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <span className="text-xs md:text-sm uppercase tracking-[0.2em] text-oar-orange font-bold">
            Présence Continentale
          </span>
          <h1
            data-testid="network-title"
            className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-white mt-4 mb-6"
          >
            {t('network.title')}
          </h1>
          <p className="max-w-2xl text-lg text-gray-300">
            {t('network.subtitle')}
          </p>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-oar-bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-oar-blue-dark border border-oar-border rounded-sm p-8 md:p-12">
            <svg viewBox="0 0 100 80" className="w-full max-w-3xl mx-auto">
              {/* Africa outline */}
              <path
                d="M30 10 L45 5 L60 8 L75 15 L80 30 L78 45 L72 55 L65 68 L55 75 L45 78 L35 72 L28 60 L22 45 L20 30 L25 18 Z"
                fill="#121212"
                stroke="#262626"
                strokeWidth="0.5"
              />
              
              {/* Country markers with labels */}
              {representations.map((rep, index) => {
                const positions = {
                  CF: { x: 52, y: 40 },
                  ML: { x: 32, y: 30 },
                  BF: { x: 36, y: 35 },
                  CM: { x: 48, y: 43 },
                  NE: { x: 42, y: 28 },
                  TD: { x: 52, y: 32 },
                };
                const pos = positions[rep.code];
                return (
                  <g key={index}>
                    <circle
                      cx={pos.x}
                      cy={pos.y}
                      r={rep.status === 'Siège' ? 4 : 2.5}
                      fill={rep.status === 'Siège' ? '#8B0000' : '#D4AF37'}
                    />
                    <text
                      x={pos.x + 4}
                      y={pos.y + 1}
                      fill="#A3A3A3"
                      fontSize="2.5"
                      fontFamily="Manrope"
                    >
                      {rep.city}
                    </text>
                  </g>
                );
              })}

              {/* Russia indicator */}
              <line x1="52" y1="40" x2="85" y2="5" stroke="#D4AF37" strokeWidth="0.3" strokeDasharray="2,2" opacity="0.6" />
              <circle cx="85" cy="5" r="3" fill="#D4AF37" />
              <text x="75" y="3" fill="#D4AF37" fontSize="3" fontFamily="Manrope">Russie</text>
            </svg>

            {/* Legend */}
            <div className="flex justify-center gap-8 mt-8">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-oar-red" />
                <span className="text-sm text-oar-text-secondary">Siège</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-oar-gold" />
                <span className="text-sm text-oar-text-secondary">Bureaux régionaux</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* National Representations */}
      <section className="py-24 bg-oar-blue-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <MapPin className="w-6 h-6 text-oar-orange" />
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white">
              {t('network.representations.title')}
            </h2>
          </div>
          <p className="text-gray-300 mb-10 max-w-2xl">
            {t('network.representations.description')}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {representations.map((rep, index) => (
              <div
                key={index}
                data-testid={`representation-${rep.code}`}
                className="bg-oar-blue border border-oar-blue-light/30 p-6 rounded-sm card-hover"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-heading text-lg text-oar-orange mb-1">{rep.country}</h3>
                    <p className="text-gray-300 text-sm">{rep.city}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-sm ${
                    rep.status === 'Siège' 
                      ? 'bg-oar-red/20 text-oar-red-light' 
                      : 'bg-oar-gold/20 text-oar-orange'
                  }`}>
                    {rep.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner Centers */}
      <section className="py-24 bg-oar-bg-secondary">
        <div className="gold-line" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16">
          <div className="flex items-center gap-3 mb-8">
            <Building2 className="w-6 h-6 text-oar-orange" />
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-oar-text-primary">
              {t('network.centers.title')}
            </h2>
          </div>
          <p className="text-oar-text-secondary mb-10 max-w-2xl">
            {t('network.centers.description')}
          </p>

          <div className="grid md:grid-cols-2 gap-6">
            {partnerCenters.map((center, index) => (
              <div
                key={index}
                className="bg-oar-blue-dark border border-oar-blue/30 p-6 rounded-sm card-hover flex items-start gap-4"
              >
                <div className="w-10 h-10 flex items-center justify-center bg-oar-orange/20 rounded-sm flex-shrink-0">
                  <Globe className="w-5 h-5 text-oar-orange" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-white mb-1">{center.name}</h3>
                  <p className="text-gray-400 text-sm">{center.country} • {center.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Affiliated Universities */}
      <section className="py-24 bg-oar-blue-dark">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-8">
            <GraduationCap className="w-6 h-6 text-oar-orange" />
            <h2 className="font-heading text-2xl md:text-3xl font-semibold text-white">
              {t('network.universities.title')}
            </h2>
          </div>
          <p className="text-gray-300 mb-10 max-w-2xl">
            {t('network.universities.description')}
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {universities.map((uni, index) => (
              <div
                key={index}
                data-testid={`university-${index}`}
                className="bg-oar-blue border border-oar-blue-light/30 p-6 rounded-sm card-hover"
              >
                <h3 className="font-heading text-lg text-oar-orange mb-2">{uni.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">{uni.country}</span>
                  <span className="text-gray-300 text-sm">{uni.programs} programmes</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default NetworkPage;
